import { useState, useEffect, useCallback, type FormEvent } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { useEmployeeDocuments } from '@/hooks/useEmployeeDocuments'
import { useEmployeeLedger } from '@/hooks/useEmployeeLedger'
import { useSchoolVehicles } from '@/hooks/useSchoolVehicles'
import { Badge, type BadgeTone } from '@/components/ui/Badge'

interface EmployeeRecord {
  membershipId: string
  userId: string
  schoolId: string
  fullName: string | null
  email: string
  phone: string | null
  jobTitle: string | null
  employmentType: 'full_time' | 'part_time' | 'casual' | null
  startDate: string | null
  relationshipType: 'employee' | 'contractor' | null
  roleCategory: 'instructor' | 'administrator' | 'receptionist' | 'finance' | 'other' | null
  canViewStudents: boolean
  canViewSchedules: boolean
  canViewFinancials: boolean
  licenceNumber: string | null
  ontarioDriverLicenceNumber: string | null
  driverLicenceExpiryDate: string | null
  instructorLicenceExpiryDate: string | null
  instructionTypes: string[] | null
  credentialVerificationStatus: 'unverified' | 'pending_review' | 'verified' | 'rejected' | null
  address: string | null
  photoPath: string | null
  assignedVehicleId: string | null
  assignedVehicle: {
    make: string; model: string; year: number | null; colour: string | null
    licencePlate: string | null; transmission: 'automatic' | 'manual' | null; photoPath: string | null
  } | null
}

const PHOTO_BUCKET = 'employee-photos'

const ROLE_CATEGORY_OPTIONS: { key: NonNullable<EmployeeRecord['roleCategory']>; label: string }[] = [
  { key: 'instructor', label: 'Instructor' },
  { key: 'administrator', label: 'Administrator' },
  { key: 'receptionist', label: 'Receptionist' },
  { key: 'finance', label: 'Finance' },
]

function formatDate(iso: string | null) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' })
}

const section: React.CSSProperties = {
  background: '#fff', border: '1px solid rgba(27,42,74,0.08)', borderRadius: 14,
  padding: '20px 22px', marginBottom: 20,
}
const sectionTitle: React.CSSProperties = {
  font: '600 15px Inter, sans-serif', color: '#1B2A4A', marginBottom: 14,
}
const label: React.CSSProperties = {
  font: '500 12.5px Inter, sans-serif', color: 'rgba(27,42,74,0.6)', display: 'block', marginBottom: 6,
}
const fieldRow: React.CSSProperties = { display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 14 }
const fieldCol: React.CSSProperties = { flex: '1 1 200px' }

const VERIFICATION_STYLE: Record<string, { tone: BadgeTone; label: string }> = {
  unverified:     { tone: 'neutral', label: 'Unverified' },
  pending_review: { tone: 'warning', label: 'Pending review' },
  verified:       { tone: 'success', label: 'Verified' },
  rejected:       { tone: 'danger',  label: 'Rejected' },
}

export default function EmployeeDetail() {
  const { membershipId } = useParams<{ membershipId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()

  const [employee, setEmployee] = useState<EmployeeRecord | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isSelf, setIsSelf] = useState(false)

  const fetchEmployee = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      // With no :membershipId param (the instructor-portal "My employment record" route),
      // resolve the caller's own active membership instead — this page is then always
      // viewed in isSelf mode, same record, read-only where the admin-only sections apply.
      const vehicleJoin = 'school_vehicles(make, model, year, colour, licence_plate, transmission, photo_path)'
      const query = membershipId
        ? supabase
            .from('school_memberships')
            .select(`id, user_id, school_id, job_title, role_category, employment_type, start_date, relationship_type, can_view_students, can_view_schedules, can_view_financials, assigned_vehicle_id, ${vehicleJoin}`)
            .eq('id', membershipId)
            .maybeSingle()
        : supabase
            .from('school_memberships')
            .select(`id, user_id, school_id, job_title, role_category, employment_type, start_date, relationship_type, can_view_students, can_view_schedules, can_view_financials, assigned_vehicle_id, ${vehicleJoin}`)
            .eq('user_id', user?.id ?? '')
            .eq('status', 'active')
            .maybeSingle()

      const { data: m, error: mErr } = await query
      if (mErr || !m) { setError('Could not load this employee.'); return }
      const mAny = m as any

      const [{ data: profile }, { data: instr }] = await Promise.all([
        supabase.from('profiles').select('full_name, email, phone').eq('id', m.user_id).maybeSingle(),
        supabase.from('instructor_profiles')
          .select('licence_number, ontario_driver_licence_number, driver_licence_expiry_date, instructor_licence_expiry_date, instruction_types, credential_verification_status, address, photo_path')
          .eq('user_id', m.user_id).maybeSingle(),
      ])

      const vehicleRow = mAny.school_vehicles ?? null

      setEmployee({
        membershipId: m.id,
        userId: m.user_id,
        schoolId: m.school_id,
        fullName: profile?.full_name ?? null,
        email: profile?.email ?? '',
        phone: profile?.phone ?? null,
        jobTitle: m.job_title,
        employmentType: m.employment_type ?? null,
        roleCategory: m.role_category ?? null,
        startDate: m.start_date,
        relationshipType: m.relationship_type,
        canViewStudents: m.can_view_students,
        canViewSchedules: m.can_view_schedules,
        canViewFinancials: m.can_view_financials,
        licenceNumber: instr?.licence_number ?? null,
        ontarioDriverLicenceNumber: instr?.ontario_driver_licence_number ?? null,
        driverLicenceExpiryDate: instr?.driver_licence_expiry_date ?? null,
        instructorLicenceExpiryDate: instr?.instructor_licence_expiry_date ?? null,
        instructionTypes: instr?.instruction_types ?? null,
        credentialVerificationStatus: instr?.credential_verification_status ?? null,
        address: instr?.address ?? null,
        photoPath: instr?.photo_path ?? null,
        assignedVehicleId: mAny.assigned_vehicle_id ?? null,
        assignedVehicle: vehicleRow ? {
          make: vehicleRow.make, model: vehicleRow.model, year: vehicleRow.year, colour: vehicleRow.colour,
          licencePlate: vehicleRow.licence_plate, transmission: vehicleRow.transmission, photoPath: vehicleRow.photo_path,
        } : null,
      })
      setIsSelf(m.user_id === user?.id)
    } finally {
      setLoading(false)
    }
  }, [membershipId, user?.id])

  useEffect(() => { fetchEmployee() }, [fetchEmployee])

  // Edit-record form state (migration 103's update_employee_details()) — covers job
  // title/role/employment type/start date/phone/permissions, and, for an instructor,
  // licensing/vehicle/address/photo. Compensation is deliberately excluded — the
  // dedicated form further down remains the only way to change it.
  const [editing, setEditing] = useState(false)
  const [editSaving, setEditSaving] = useState(false)
  const [editError, setEditError] = useState('')
  const [editPhotoUploading, setEditPhotoUploading] = useState(false)
  const [editJobTitle, setEditJobTitle] = useState('')
  const [editRoleCategory, setEditRoleCategory] = useState<NonNullable<EmployeeRecord['roleCategory']> | ''>('')
  const [editEmploymentType, setEditEmploymentType] = useState<NonNullable<EmployeeRecord['employmentType']> | ''>('')
  const [editStartDate, setEditStartDate] = useState('')
  const [editPhone, setEditPhone] = useState('')
  const [editCanViewStudents, setEditCanViewStudents] = useState(false)
  const [editCanViewSchedules, setEditCanViewSchedules] = useState(false)
  const [editCanViewFinancials, setEditCanViewFinancials] = useState(false)
  const [editLicenceNumber, setEditLicenceNumber] = useState('')
  const [editLicenceExpiry, setEditLicenceExpiry] = useState('')
  const [editDriverLicenceNumber, setEditDriverLicenceNumber] = useState('')
  const [editDriverLicenceExpiry, setEditDriverLicenceExpiry] = useState('')
  const [editAddress, setEditAddress] = useState('')
  const [editPhotoPath, setEditPhotoPath] = useState<string | null>(null)

  // Vehicle (re)assignment — a separate action (assign_vehicle_to_employee(), migration
  // 104), not bundled into the edit form above: it's its own focused RPC, and showing it
  // as an always-visible control (not gated behind "Edit") matches how often a school
  // actually changes this (whenever a car is swapped), separate from editing the rest of
  // the employment record.
  const [assigningVehicle, setAssigningVehicle] = useState(false)
  const [vehicleAssignError, setVehicleAssignError] = useState('')

  function startEditing() {
    if (!employee) return
    setEditJobTitle(employee.jobTitle ?? '')
    setEditRoleCategory(employee.roleCategory ?? '')
    setEditEmploymentType(employee.employmentType ?? '')
    setEditStartDate(employee.startDate ?? '')
    setEditPhone(employee.phone ?? '')
    setEditCanViewStudents(employee.canViewStudents)
    setEditCanViewSchedules(employee.canViewSchedules)
    setEditCanViewFinancials(employee.canViewFinancials)
    setEditLicenceNumber(employee.licenceNumber ?? '')
    setEditLicenceExpiry(employee.instructorLicenceExpiryDate ?? '')
    setEditDriverLicenceNumber(employee.ontarioDriverLicenceNumber ?? '')
    setEditDriverLicenceExpiry(employee.driverLicenceExpiryDate ?? '')
    setEditAddress(employee.address ?? '')
    setEditPhotoPath(employee.photoPath)
    setEditError('')
    setEditing(true)
  }

  async function handleEditPhotoChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file || !employee) return
    setEditPhotoUploading(true)
    try {
      const ext = (file.name.split('.').pop() || 'jpg').toLowerCase()
      const path = `${employee.schoolId}/${crypto.randomUUID()}.${ext}`
      const { error: upErr } = await supabase.storage.from(PHOTO_BUCKET).upload(path, file, { contentType: file.type, upsert: false })
      if (upErr) { setEditError('Could not upload the photo. Please try again.'); return }
      setEditPhotoPath(path)
    } finally {
      setEditPhotoUploading(false)
    }
  }

  async function handleSaveEdit(e: FormEvent) {
    e.preventDefault()
    setEditError('')
    if (!employee) return
    if (!editJobTitle.trim()) { setEditError('Job title is required.'); return }
    if (!editRoleCategory) { setEditError('Choose a role category.'); return }
    if (!editStartDate) { setEditError('Start date is required.'); return }
    if (!editEmploymentType) { setEditError('Choose an employment type.'); return }

    setEditSaving(true)
    try {
      const { data, error: rpcError } = await supabase.rpc('update_employee_details', {
        p_school_membership_id: employee.membershipId,
        p_job_title: editJobTitle.trim(),
        p_role_category: editRoleCategory,
        p_start_date: editStartDate,
        p_employment_type: editEmploymentType,
        p_phone: editPhone.trim() || null,
        p_can_view_students: editCanViewStudents,
        p_can_view_schedules: editCanViewSchedules,
        p_can_view_financials: editCanViewFinancials,
        p_ontario_driver_licence_number: editDriverLicenceNumber.trim() || null,
        p_driver_licence_expiry_date: editDriverLicenceExpiry || null,
        p_instructor_licence_number: editLicenceNumber.trim() || null,
        p_instructor_licence_expiry_date: editLicenceExpiry || null,
        p_address: editAddress.trim() || null,
        p_photo_path: editPhotoPath,
      })
      if (rpcError) { setEditError('Could not save changes. Please try again.'); return }
      const result = data as { ok: boolean; code?: string }
      if (!result?.ok) {
        setEditError(
          result?.code === 'not_school_admin' ? 'You need to be an active admin of this school to edit employees.'
          : result?.code === 'not_an_employee' ? 'Only an employee record can be edited this way.'
          : result?.code === 'photo_not_found' ? 'That photo could not be found — try uploading it again.'
          : 'Could not save changes. Please try again.'
        )
        return
      }
      setEditing(false)
      await fetchEmployee()
    } finally {
      setEditSaving(false)
    }
  }

  const { vehicles: fleetVehicles, loading: fleetLoading, photoUrl: fleetPhotoUrl } = useSchoolVehicles(employee?.schoolId ?? null)

  async function handleAssignVehicle(vehicleId: string) {
    if (!employee) return
    setVehicleAssignError('')
    setAssigningVehicle(true)
    try {
      const { data, error: rpcError } = await supabase.rpc('assign_vehicle_to_employee', {
        p_school_membership_id: employee.membershipId,
        p_vehicle_id: vehicleId || undefined,
      })
      if (rpcError) { setVehicleAssignError('Could not change the vehicle assignment. Please try again.'); return }
      const result = data as { ok: boolean; code?: string }
      if (!result?.ok) {
        setVehicleAssignError(
          result?.code === 'vehicle_already_assigned' ? 'That vehicle is already assigned to another active employee.'
          : result?.code === 'vehicle_not_found' ? 'That vehicle is no longer available.'
          : 'Could not change the vehicle assignment. Please try again.'
        )
        return
      }
      await fetchEmployee()
    } finally {
      setAssigningVehicle(false)
    }
  }

  const {
    documents, loading: docsLoading, uploading, uploadDocument, documentUrl,
  } = useEmployeeDocuments(membershipId ?? null)
  const {
    compensation, timeEntries, earningsEntries, loading: ledgerLoading,
    savingCompensation, loggingTime, loggingEarnings, settling,
    saveCompensation, logTimeEntry, logEarningsEntry, settlePeriod,
  } = useEmployeeLedger(membershipId ?? null)

  // Document upload form state
  const [docType, setDocType] = useState<'employment_contract' | 'instructor_licence' | 'drivers_licence' | 'other'>('employment_contract')
  const [docTitle, setDocTitle] = useState('')
  const [docFile, setDocFile] = useState<File | null>(null)
  const [docError, setDocError] = useState('')
  const [docNotice, setDocNotice] = useState('')

  const handleUploadDocument = async (e: FormEvent) => {
    e.preventDefault()
    setDocError(''); setDocNotice('')
    if (!docFile) { setDocError('Choose a file to upload.'); return }
    if (!docTitle.trim()) { setDocError('Give this document a title.'); return }
    const result = await uploadDocument(docFile, docType, docTitle.trim())
    if (!result.ok) { setDocError('Could not upload this document. Please try again.'); return }
    setDocTitle(''); setDocFile(null); setDocNotice('Document uploaded.')
  }

  const handleOpenDocument = async (id: string) => {
    const url = await documentUrl(id)
    if (url) window.open(url, '_blank')
  }

  // Compensation form state
  const [compModel, setCompModel] = useState<'hourly' | 'salary'>('hourly')
  const [compAmount, setCompAmount] = useState('')
  const [compEffectiveDate, setCompEffectiveDate] = useState('')
  const [compFrequency, setCompFrequency] = useState<'weekly' | 'biweekly' | 'monthly'>('biweekly')
  const [compError, setCompError] = useState('')
  const [compNotice, setCompNotice] = useState('')

  const handleSaveCompensation = async (e: FormEvent) => {
    e.preventDefault()
    setCompError(''); setCompNotice('')
    const amount = parseFloat(compAmount)
    if (!Number.isFinite(amount) || amount <= 0) { setCompError('Enter a valid amount.'); return }
    if (!compEffectiveDate) { setCompError('Set an effective date.'); return }
    const result = await saveCompensation({
      compensationModel: compModel,
      hourlyRate: compModel === 'hourly' ? amount : null,
      salaryAmount: compModel === 'salary' ? amount : null,
      effectiveDate: compEffectiveDate,
      paymentFrequency: compFrequency,
    })
    if (!result.ok) {
      setCompError(result.code === 'not_an_employee'
        ? 'Compensation terms can only be set for an employee, not a contractor.'
        : 'Could not save compensation terms. Please try again.')
      return
    }
    setCompNotice('Compensation terms saved.')
  }

  // Time entry form state
  const [timeDate, setTimeDate] = useState('')
  const [timeHours, setTimeHours] = useState('')
  const [timeNote, setTimeNote] = useState('')
  const [timeError, setTimeError] = useState('')

  const handleLogTime = async (e: FormEvent) => {
    e.preventDefault()
    setTimeError('')
    const hours = parseFloat(timeHours)
    if (!timeDate) { setTimeError('Pick the work date.'); return }
    if (!Number.isFinite(hours) || hours <= 0 || hours > 24) { setTimeError('Enter hours between 0 and 24.'); return }
    const result = await logTimeEntry({ workDate: timeDate, hours, note: timeNote.trim() || undefined })
    if (!result.ok) { setTimeError('Could not log this entry. Please try again.'); return }
    setTimeDate(''); setTimeHours(''); setTimeNote('')
  }

  // Earnings entry form state (admin only)
  const [earnStart, setEarnStart] = useState('')
  const [earnEnd, setEarnEnd] = useState('')
  const [earnAmount, setEarnAmount] = useState('')
  const [earnError, setEarnError] = useState('')

  const handleLogEarnings = async (e: FormEvent) => {
    e.preventDefault()
    setEarnError('')
    const amount = parseFloat(earnAmount)
    if (!earnStart || !earnEnd) { setEarnError('Set the pay period start and end dates.'); return }
    if (!Number.isFinite(amount) || amount <= 0) { setEarnError('Enter a valid gross amount.'); return }
    const result = await logEarningsEntry({ periodStart: earnStart, periodEnd: earnEnd, grossAmount: amount })
    if (!result.ok) {
      setEarnError(result.code === 'not_school_admin'
        ? 'Only the school admin can record an earnings entry.'
        : 'Could not record this entry. Please try again.')
      return
    }
    setEarnStart(''); setEarnEnd(''); setEarnAmount('')
  }

  // Settle period form state (hourly employee instructors only) — sums already
  // auto-logged hours in the range into one earnings entry; no amount is typed by hand.
  const [settleStart, setSettleStart] = useState('')
  const [settleEnd, setSettleEnd] = useState('')
  const [settleError, setSettleError] = useState('')
  const [settleNotice, setSettleNotice] = useState('')

  const handleSettlePeriod = async (e: FormEvent) => {
    e.preventDefault()
    setSettleError(''); setSettleNotice('')
    if (!settleStart || !settleEnd) { setSettleError('Set the pay period start and end dates.'); return }
    const result = await settlePeriod({ periodStart: settleStart, periodEnd: settleEnd })
    if (!result.ok) {
      setSettleError(
        result.code === 'no_hours_in_period' ? 'No logged hours in this period — nothing to settle.'
        : result.code === 'hourly_rate_not_set' ? 'This employee has no hourly rate set yet.'
        : 'Could not settle this period. Please try again.'
      )
      return
    }
    setSettleNotice(`Settled ${result.hours}h → $${result.gross_amount?.toFixed(2)}.`)
    setSettleStart(''); setSettleEnd('')
  }

  if (loading) {
    return <div style={{ font: '400 14px Inter, sans-serif', color: 'rgba(27,42,74,0.4)', padding: '24px 0' }}>Loading…</div>
  }
  if (error || !employee) {
    return <div className='banner-error'>{error ?? 'Employee not found.'}</div>
  }

  // Driven primarily by role_category (the reliable signal) — also true if the free-text
  // job title happens to mention "instructor", same gate Instructors.tsx's invite form uses.
  const isInstructor = employee.roleCategory === 'instructor' || (employee.jobTitle ?? '').toLowerCase().includes('instructor')
  // Hours/earnings automation scope (migration 091): a contractor (always an instructor by
  // business rule, migration 078 — never carries role_category) or an employee explicitly
  // categorized as an instructor. Everyone else keeps the fully manual ledger untouched.
  const isAutoInstructor = employee.relationshipType === 'contractor' || employee.roleCategory === 'instructor'
  const isHourlyInstructorEmployee = isAutoInstructor && employee.relationshipType === 'employee' && compensation?.compensationModel === 'hourly'
  const isSalariedInstructorEmployee = isAutoInstructor && employee.relationshipType === 'employee' && compensation?.compensationModel === 'salary'
  const verif = employee.credentialVerificationStatus ? VERIFICATION_STYLE[employee.credentialVerificationStatus] : null
  const photoUrl = employee.photoPath ? supabase.storage.from(PHOTO_BUCKET).getPublicUrl(employee.photoPath).data.publicUrl : null
  const editPhotoUrl = editPhotoPath ? supabase.storage.from(PHOTO_BUCKET).getPublicUrl(editPhotoPath).data.publicUrl : null
  const canEdit = !isSelf && employee.relationshipType === 'employee'

  return (
    <div>
      {membershipId && (
        <button
          onClick={() => navigate('/app/school/instructors')}
          style={{ background: 'none', border: 'none', font: '500 13px Inter, sans-serif', color: '#0F7C6C', cursor: 'pointer', marginBottom: 14, padding: 0 }}
        >
          ← Back to Instructors
        </button>
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{
            width: 48, height: 48, borderRadius: '50%', overflow: 'hidden', flexShrink: 0,
            background: 'rgba(27,42,74,0.05)', border: '1px solid rgba(27,42,74,0.1)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {photoUrl ? (
              <img src={photoUrl} alt='' style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <span style={{ font: '600 16px Inter, sans-serif', color: 'rgba(27,42,74,0.3)' }}>
                {(employee.fullName ?? employee.email).charAt(0).toUpperCase()}
              </span>
            )}
          </div>
          <div>
            <div style={{ font: '600 24px Lora, serif', color: '#1B2A4A', marginBottom: 2 }}>
              {employee.fullName ?? employee.email}
            </div>
            <div style={{ font: '400 14px Inter, sans-serif', color: 'rgba(27,42,74,0.55)' }}>
              {employee.jobTitle ?? 'Employee'} · {employee.email}
            </div>
          </div>
        </div>
        {canEdit && !editing && (
          <button onClick={startEditing} className='btn-teal' style={{ width: 'auto', padding: '9px 18px' }}>
            Edit
          </button>
        )}
      </div>

      {editing ? (
        <form onSubmit={handleSaveEdit} style={section}>
          <div style={sectionTitle}>Edit employee record</div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 18 }}>
            <div style={{
              width: 56, height: 56, borderRadius: '50%', overflow: 'hidden', flexShrink: 0,
              background: 'rgba(27,42,74,0.05)', border: '1px solid rgba(27,42,74,0.1)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {editPhotoUrl ? (
                <img src={editPhotoUrl} alt='' style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <span style={{ font: '600 18px Inter, sans-serif', color: 'rgba(27,42,74,0.3)' }}>
                  {(employee.fullName ?? employee.email).charAt(0).toUpperCase()}
                </span>
              )}
            </div>
            <label style={{
              display: 'inline-block', padding: '8px 16px', background: '#fff',
              border: '1px solid rgba(27,42,74,0.15)', borderRadius: 8,
              font: '600 12.5px Inter, sans-serif', color: '#1B2A4A',
              cursor: editPhotoUploading ? 'not-allowed' : 'pointer', opacity: editPhotoUploading ? 0.6 : 1,
            }}>
              {editPhotoUploading ? 'Uploading…' : editPhotoPath ? 'Replace photo' : 'Upload photo'}
              <input type='file' accept='image/*' onChange={handleEditPhotoChange} disabled={editPhotoUploading} style={{ display: 'none' }} />
            </label>
          </div>

          <div style={fieldRow}>
            <div style={fieldCol}>
              <div style={label}>Role category</div>
              <select value={editRoleCategory} onChange={e => setEditRoleCategory(e.target.value as any)} className='field-input'>
                <option value='' disabled>Select a category…</option>
                {ROLE_CATEGORY_OPTIONS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
              </select>
            </div>
            <div style={fieldCol}>
              <div style={label}>Job title</div>
              <input value={editJobTitle} onChange={e => setEditJobTitle(e.target.value)} className='field-input' />
            </div>
          </div>
          <div style={fieldRow}>
            <div style={fieldCol}>
              <div style={label}>Employment type</div>
              <select value={editEmploymentType} onChange={e => setEditEmploymentType(e.target.value as any)} className='field-input'>
                <option value='' disabled>Select…</option>
                <option value='full_time'>Full-time</option>
                <option value='part_time'>Part-time</option>
              </select>
            </div>
            <div style={fieldCol}>
              <div style={label}>Start date</div>
              <input type='date' value={editStartDate} onChange={e => setEditStartDate(e.target.value)} className='field-input' />
            </div>
            <div style={fieldCol}>
              <div style={label}>Phone</div>
              <input value={editPhone} onChange={e => setEditPhone(e.target.value)} className='field-input' />
            </div>
          </div>

          <div style={label}>Permissions</div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 18 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, font: '400 13px Inter, sans-serif', color: '#1B2A4A' }}>
              <input type='checkbox' checked={editCanViewStudents} onChange={e => setEditCanViewStudents(e.target.checked)} /> View students
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, font: '400 13px Inter, sans-serif', color: '#1B2A4A' }}>
              <input type='checkbox' checked={editCanViewSchedules} onChange={e => setEditCanViewSchedules(e.target.checked)} /> View schedules
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, font: '400 13px Inter, sans-serif', color: '#1B2A4A' }}>
              <input type='checkbox' checked={editCanViewFinancials} onChange={e => setEditCanViewFinancials(e.target.checked)} /> View financial records
            </label>
          </div>

          {editRoleCategory === 'instructor' && (
            <>
              <div style={{ font: '600 13px Inter, sans-serif', color: '#1B2A4A', marginBottom: 10 }}>Instructor licensing</div>
              <div style={fieldRow}>
                <div style={fieldCol}><div style={label}>Instructor licence #</div><input value={editLicenceNumber} onChange={e => setEditLicenceNumber(e.target.value)} className='field-input' /></div>
                <div style={fieldCol}><div style={label}>Instructor licence expiry</div><input type='date' value={editLicenceExpiry} onChange={e => setEditLicenceExpiry(e.target.value)} className='field-input' /></div>
              </div>
              <div style={fieldRow}>
                <div style={fieldCol}><div style={label}>Driver's licence #</div><input value={editDriverLicenceNumber} onChange={e => setEditDriverLicenceNumber(e.target.value)} className='field-input' /></div>
                <div style={fieldCol}><div style={label}>Driver's licence expiry</div><input type='date' value={editDriverLicenceExpiry} onChange={e => setEditDriverLicenceExpiry(e.target.value)} className='field-input' /></div>
              </div>
              <div style={{ marginBottom: 14 }}><div style={label}>Address</div><input value={editAddress} onChange={e => setEditAddress(e.target.value)} className='field-input' /></div>
            </>
          )}

          {editError && <div className='banner-error' style={{ marginBottom: 14 }}>{editError}</div>}

          <div style={{ display: 'flex', gap: 10 }}>
            <button type='submit' className='btn-teal' disabled={editSaving} style={{ width: 'auto', padding: '10px 22px' }}>
              {editSaving ? 'Saving…' : 'Save changes'}
            </button>
            <button
              type='button'
              onClick={() => setEditing(false)}
              disabled={editSaving}
              style={{ background: 'none', border: 'none', font: '600 13px Inter, sans-serif', color: 'rgba(27,42,74,0.45)', cursor: 'pointer' }}
            >
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <>
          {/* Employment record */}
          <div style={section}>
            <div style={sectionTitle}>Employment record</div>
            <div style={fieldRow}>
              <div style={fieldCol}><div style={label}>Job title</div><div>{employee.jobTitle ?? '—'}</div></div>
              <div style={fieldCol}><div style={label}>Start date</div><div>{formatDate(employee.startDate)}</div></div>
              <div style={fieldCol}><div style={label}>Phone</div><div>{employee.phone ?? '—'}</div></div>
            </div>
            <div style={label}>Permissions</div>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <span style={{ font: '400 13px Inter, sans-serif', color: employee.canViewStudents ? '#0F7C6C' : 'rgba(27,42,74,0.4)' }}>
                {employee.canViewStudents ? '✓' : '✕'} Students
              </span>
              <span style={{ font: '400 13px Inter, sans-serif', color: employee.canViewSchedules ? '#0F7C6C' : 'rgba(27,42,74,0.4)' }}>
                {employee.canViewSchedules ? '✓' : '✕'} Schedules
              </span>
              <span style={{ font: '400 13px Inter, sans-serif', color: employee.canViewFinancials ? '#0F7C6C' : 'rgba(27,42,74,0.4)' }}>
                {employee.canViewFinancials ? '✓' : '✕'} Financial records
              </span>
            </div>
          </div>

          {/* Credentials */}
          {isInstructor && (
            <div style={section}>
              <div style={sectionTitle}>
                Instructor credentials{' '}
                {verif && (
                  <span style={{ marginLeft: 10, verticalAlign: 'middle', display: 'inline-block' }}>
                    <Badge label={verif.label} tone={verif.tone} />
                  </span>
                )}
              </div>
              <div style={fieldRow}>
                <div style={fieldCol}><div style={label}>Instructor licence #</div><div>{employee.licenceNumber ?? '—'}</div></div>
                <div style={fieldCol}><div style={label}>Instructor licence expiry</div><div>{formatDate(employee.instructorLicenceExpiryDate)}</div></div>
                <div style={fieldCol}><div style={label}>Driver's licence #</div><div>{employee.ontarioDriverLicenceNumber ?? '—'}</div></div>
                <div style={fieldCol}><div style={label}>Driver's licence expiry</div><div>{formatDate(employee.driverLicenceExpiryDate)}</div></div>
              </div>
              {employee.instructionTypes && employee.instructionTypes.length > 0 && (
                <div style={{ marginBottom: 14 }}><div style={label}>Instruction types</div><div>{employee.instructionTypes.join(', ')}</div></div>
              )}
              <div><div style={label}>Address</div><div>{employee.address ?? '—'}</div></div>
            </div>
          )}

          {/* Assigned vehicle — a school-owned fleet car (migration 104), never self-
              reported by the employee. Read-only display + a separate assignment control,
              admin-only (never shown to the employee viewing their own record). */}
          {employee.relationshipType === 'employee' && (
            <div style={section}>
              <div style={sectionTitle}>Assigned vehicle</div>
              {employee.assignedVehicle ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: !isSelf ? 16 : 0 }}>
                  {(() => {
                    const url = fleetPhotoUrl(employee.assignedVehicle.photoPath)
                    return (
                      <div style={{
                        width: 48, height: 48, borderRadius: 8, overflow: 'hidden', flexShrink: 0,
                        background: 'rgba(27,42,74,0.05)', border: '1px solid rgba(27,42,74,0.1)',
                      }}>
                        {url && <img src={url} alt='' style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                      </div>
                    )
                  })()}
                  <div>
                    <div style={{ font: '600 14px Inter, sans-serif', color: '#1B2A4A' }}>
                      {[employee.assignedVehicle.year, employee.assignedVehicle.make, employee.assignedVehicle.model].filter(Boolean).join(' ')}
                    </div>
                    <div style={{ font: '400 12.5px Inter, sans-serif', color: 'rgba(27,42,74,0.55)', marginTop: 2 }}>
                      {[employee.assignedVehicle.colour, employee.assignedVehicle.transmission, employee.assignedVehicle.licencePlate].filter(Boolean).join(' · ') || '—'}
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ font: '400 13px Inter, sans-serif', color: 'rgba(27,42,74,0.4)', marginBottom: !isSelf ? 16 : 0 }}>
                  No vehicle assigned.
                </div>
              )}
              {!isSelf && (
                <div>
                  <div style={label}>{employee.assignedVehicle ? 'Reassign' : 'Assign a vehicle'}</div>
                  <select
                    value={employee.assignedVehicleId ?? ''}
                    onChange={e => handleAssignVehicle(e.target.value)}
                    className='field-input'
                    style={{ maxWidth: 320 }}
                    disabled={assigningVehicle || fleetLoading}
                  >
                    <option value=''>No vehicle assigned</option>
                    {fleetVehicles.filter(v => v.isActive || v.id === employee.assignedVehicleId).map(v => (
                      <option key={v.id} value={v.id}>
                        {[v.year, v.make, v.model].filter(Boolean).join(' ')}{v.licencePlate ? ` — ${v.licencePlate}` : ''}
                      </option>
                    ))}
                  </select>
                  {vehicleAssignError && <div className='banner-error' style={{ marginTop: 10 }}>{vehicleAssignError}</div>}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Documents */}
      <div style={section}>
        <div style={sectionTitle}>Documents</div>

        {docsLoading && <div style={{ font: '400 13px Inter, sans-serif', color: 'rgba(27,42,74,0.4)' }}>Loading…</div>}
        {!docsLoading && documents.length === 0 && (
          <div style={{ font: '400 13px Inter, sans-serif', color: 'rgba(27,42,74,0.4)', marginBottom: 14 }}>No documents uploaded yet.</div>
        )}
        {documents.map(d => (
          <div key={d.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid rgba(27,42,74,0.05)' }}>
            <div>
              <div style={{ font: '500 13.5px Inter, sans-serif', color: '#1B2A4A' }}>{d.title}</div>
              <div style={{ font: '400 12px Inter, sans-serif', color: 'rgba(27,42,74,0.45)', textTransform: 'capitalize' }}>
                {d.documentType.replace(/_/g, ' ')} · {formatDate(d.createdAt)}
              </div>
            </div>
            <button
              onClick={() => handleOpenDocument(d.id)}
              style={{ background: 'none', border: 'none', font: '600 12.5px Inter, sans-serif', color: '#0F7C6C', cursor: 'pointer' }}
            >
              View
            </button>
          </div>
        ))}

        {!isSelf && (
          <form onSubmit={handleUploadDocument} style={{ marginTop: 16, display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div style={fieldCol}>
              <div style={label}>Document type</div>
              <select value={docType} onChange={e => setDocType(e.target.value as any)} className='field-input'>
                <option value='employment_contract'>Employment contract</option>
                <option value='instructor_licence'>Instructor licence</option>
                <option value='drivers_licence'>Driver's licence</option>
                <option value='other'>Other</option>
              </select>
            </div>
            <div style={fieldCol}>
              <div style={label}>Title</div>
              <input value={docTitle} onChange={e => setDocTitle(e.target.value)} className='field-input' placeholder='e.g. Signed contract' />
            </div>
            <div style={fieldCol}>
              <div style={label}>File</div>
              <input type='file' onChange={e => setDocFile(e.target.files?.[0] ?? null)} className='field-input' accept='.pdf,.jpg,.jpeg,.png' />
            </div>
            <button className='btn-teal' type='submit' disabled={uploading} style={{ width: 'auto', padding: '11px 22px' }}>
              {uploading ? 'Uploading…' : 'Upload'}
            </button>
          </form>
        )}
        {docError && <div className='banner-error' style={{ marginTop: 10 }}>{docError}</div>}
        {docNotice && <div style={{ marginTop: 10, font: '400 13px Inter, sans-serif', color: '#0F7C6C' }}>{docNotice}</div>}
      </div>

      {/* Compensation */}
      {employee.relationshipType === 'employee' && (
        <div style={section}>
          <div style={sectionTitle}>Compensation terms</div>
          {ledgerLoading && <div style={{ font: '400 13px Inter, sans-serif', color: 'rgba(27,42,74,0.4)' }}>Loading…</div>}
          {!ledgerLoading && compensation?.compensationModel && (
            <div style={fieldRow}>
              <div style={fieldCol}><div style={label}>Model</div><div style={{ textTransform: 'capitalize' }}>{compensation.compensationModel}</div></div>
              <div style={fieldCol}>
                <div style={label}>Amount</div>
                <div>{compensation.compensationModel === 'hourly'
                  ? `$${compensation.hourlyRate?.toFixed(2)}/hr`
                  : `$${compensation.salaryAmount?.toFixed(2)}`}</div>
              </div>
              <div style={fieldCol}><div style={label}>Frequency</div><div style={{ textTransform: 'capitalize' }}>{compensation.paymentFrequency ?? '—'}</div></div>
              <div style={fieldCol}><div style={label}>Effective</div><div>{formatDate(compensation.compensationEffectiveDate)}</div></div>
            </div>
          )}
          {!ledgerLoading && !compensation?.compensationModel && (
            <div style={{ font: '400 13px Inter, sans-serif', color: 'rgba(27,42,74,0.4)', marginBottom: 14 }}>No compensation terms set yet.</div>
          )}

          {!isSelf && (
            <form onSubmit={handleSaveCompensation} style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div style={fieldCol}>
                <div style={label}>Model</div>
                <select value={compModel} onChange={e => setCompModel(e.target.value as any)} className='field-input'>
                  <option value='hourly'>Hourly</option>
                  <option value='salary'>Salary</option>
                </select>
              </div>
              <div style={fieldCol}>
                <div style={label}>{compModel === 'hourly' ? 'Hourly rate' : 'Salary amount'}</div>
                <input type='number' min='0.01' step='0.01' value={compAmount} onChange={e => setCompAmount(e.target.value)} className='field-input' />
              </div>
              <div style={fieldCol}>
                <div style={label}>Payment frequency</div>
                <select value={compFrequency} onChange={e => setCompFrequency(e.target.value as any)} className='field-input'>
                  <option value='weekly'>Weekly</option>
                  <option value='biweekly'>Biweekly</option>
                  <option value='monthly'>Monthly</option>
                </select>
              </div>
              <div style={fieldCol}>
                <div style={label}>Effective date</div>
                <input type='date' value={compEffectiveDate} onChange={e => setCompEffectiveDate(e.target.value)} className='field-input' />
              </div>
              <button className='btn-teal' type='submit' disabled={savingCompensation} style={{ width: 'auto', padding: '11px 22px' }}>
                {savingCompensation ? 'Saving…' : 'Save'}
              </button>
            </form>
          )}
          {compError && <div className='banner-error' style={{ marginTop: 10 }}>{compError}</div>}
          {compNotice && <div style={{ marginTop: 10, font: '400 13px Inter, sans-serif', color: '#0F7C6C' }}>{compNotice}</div>}
        </div>
      )}

      {/* Hours logged */}
      <div style={section}>
        <div style={sectionTitle}>Hours logged</div>
        {timeEntries.length === 0 && (
          <div style={{ font: '400 13px Inter, sans-serif', color: 'rgba(27,42,74,0.4)', marginBottom: 14 }}>No hours logged yet.</div>
        )}
        {timeEntries.map(t => (
          <div key={t.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid rgba(27,42,74,0.05)', font: '400 13px Inter, sans-serif', color: '#1B2A4A' }}>
            <span>
              {formatDate(t.workDate)}{t.note ? ` · ${t.note}` : ''}
              {t.lessonId && <span style={{ marginLeft: 8, font: '500 11px Inter, sans-serif', color: '#0F7C6C' }}>· from lesson</span>}
            </span>
            <span style={{ fontWeight: 600 }}>{t.hours}h</span>
          </div>
        ))}

        {isAutoInstructor ? (
          <div style={{ marginTop: 16, font: '400 12.5px Inter, sans-serif', color: 'rgba(27,42,74,0.45)' }}>
            Hours are logged automatically when this instructor completes a lesson — there's nothing to add by hand.
          </div>
        ) : (
          <form onSubmit={handleLogTime} style={{ marginTop: 16, display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div style={fieldCol}><div style={label}>Date</div><input type='date' value={timeDate} onChange={e => setTimeDate(e.target.value)} className='field-input' /></div>
            <div style={fieldCol}><div style={label}>Hours</div><input type='number' min='0.25' max='24' step='0.25' value={timeHours} onChange={e => setTimeHours(e.target.value)} className='field-input' /></div>
            <div style={{ flex: '1 1 240px' }}><div style={label}>Note (optional)</div><input value={timeNote} onChange={e => setTimeNote(e.target.value)} className='field-input' /></div>
            <button className='btn-teal' type='submit' disabled={loggingTime} style={{ width: 'auto', padding: '11px 22px' }}>
              {loggingTime ? 'Adding…' : 'Add entry'}
            </button>
          </form>
        )}
        {timeError && <div className='banner-error' style={{ marginTop: 10 }}>{timeError}</div>}
      </div>

      {/* Earnings recorded (a.k.a. payment ledger). Salaried instructor employees have no
          earnings concept at all — pay doesn't depend on hours — so the section is
          omitted entirely for them. */}
      {!isSalariedInstructorEmployee && (!isSelf || isAutoInstructor) && (
        <div style={section}>
          <div style={sectionTitle}>Earnings recorded</div>
          {earningsEntries.length === 0 && (
            <div style={{ font: '400 13px Inter, sans-serif', color: 'rgba(27,42,74,0.4)', marginBottom: 14 }}>No earnings recorded yet.</div>
          )}
          {earningsEntries.map(e => (
            <div key={e.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid rgba(27,42,74,0.05)', font: '400 13px Inter, sans-serif', color: '#1B2A4A' }}>
              <span>
                {formatDate(e.periodStart)} – {formatDate(e.periodEnd)}
                {e.lessonId && <span style={{ marginLeft: 8, font: '500 11px Inter, sans-serif', color: '#0F7C6C' }}>· from lesson</span>}
              </span>
              <span style={{ fontWeight: 600 }}>${e.grossAmount.toFixed(2)}</span>
            </div>
          ))}

          {isAutoInstructor ? (
            !isSelf && isHourlyInstructorEmployee && (
              <form onSubmit={handleSettlePeriod} style={{ marginTop: 16, display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <div style={fieldCol}><div style={label}>Settle period start</div><input type='date' value={settleStart} onChange={e => setSettleStart(e.target.value)} className='field-input' /></div>
                <div style={fieldCol}><div style={label}>Settle period end</div><input type='date' value={settleEnd} onChange={e => setSettleEnd(e.target.value)} className='field-input' /></div>
                <button className='btn-teal' type='submit' disabled={settling} style={{ width: 'auto', padding: '11px 22px' }}>
                  {settling ? 'Settling…' : 'Settle period'}
                </button>
              </form>
            )
          ) : !isSelf && (
            <form onSubmit={handleLogEarnings} style={{ marginTop: 16, display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div style={fieldCol}><div style={label}>Period start</div><input type='date' value={earnStart} onChange={e => setEarnStart(e.target.value)} className='field-input' /></div>
              <div style={fieldCol}><div style={label}>Period end</div><input type='date' value={earnEnd} onChange={e => setEarnEnd(e.target.value)} className='field-input' /></div>
              <div style={fieldCol}><div style={label}>Gross amount</div><input type='number' min='0.01' step='0.01' value={earnAmount} onChange={e => setEarnAmount(e.target.value)} className='field-input' /></div>
              <button className='btn-teal' type='submit' disabled={loggingEarnings} style={{ width: 'auto', padding: '11px 22px' }}>
                {loggingEarnings ? 'Adding…' : 'Add entry'}
              </button>
            </form>
          )}
          {isAutoInstructor && employee.relationshipType === 'contractor' && (
            <div style={{ marginTop: 16, font: '400 12.5px Inter, sans-serif', color: 'rgba(27,42,74,0.45)' }}>
              Earnings post automatically — one entry per completed lesson, at this contractor's hourly rate.
            </div>
          )}
          {earnError && <div className='banner-error' style={{ marginTop: 10 }}>{earnError}</div>}
          {settleError && <div className='banner-error' style={{ marginTop: 10 }}>{settleError}</div>}
          {settleNotice && <div style={{ marginTop: 10, font: '400 13px Inter, sans-serif', color: '#0F7C6C' }}>{settleNotice}</div>}
        </div>
      )}
    </div>
  )
}
